import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchSubCategories, updateSubCategory, clearError, clearSuccess } from '../../../redux/Slices/AdminCategoriesSlice';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const resolveImageUrl = (value) => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    const trimmedApi = BASE_URL.replace(/\/api$/i, '');
    const base = trimmedApi.endsWith('/') ? trimmedApi.slice(0, -1) : trimmedApi;
    const path = value.startsWith('/') ? value : `/${value}`;
    return `${base}${path}`;
};

const AdminEditSubCategory = () => {
    const { setTitle } = useTitle();
    const { categoryId, subCategoryId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { subCategories, updating, error, success } = useSelector((state) => state.adminCategories);
    
    const [formData, setFormData] = useState({
        name_ar: '',
        name_en: '',
        image: null,
        imagePreview: ''
    });
    const [imageLoading, setImageLoading] = useState(false);

    useEffect(() => {
        setTitle('تعديل الفئة الفرعية');
        if (subCategories.length === 0) {
            dispatch(fetchSubCategories({ categoryId }));
        }
    }, [setTitle, dispatch, categoryId, subCategories.length]);

    useEffect(() => {
        const subCategory = subCategories.find(sub => sub.id === parseInt(subCategoryId));
        if (subCategory) {
            const existingImageUrl = subCategory.image_url || subCategory.image || '';
            const resolvedImageUrl = existingImageUrl ? resolveImageUrl(existingImageUrl) : '';
            setFormData({
                name_ar: subCategory.name?.ar || '',
                name_en: subCategory.name?.en || '',
                image: existingImageUrl ? 'existing' : null, // Mark existing image
                imagePreview: resolvedImageUrl
            });
            setImageLoading(false);
        }
    }, [subCategories, subCategoryId]);

    useEffect(() => {
        if (error) {
            const errorMessage = typeof error === 'string' ? error : error?.message || 'حدث خطأ';
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            const successMessage = typeof success === 'string' ? success : 'تم التحديث بنجاح';
            toast.success(successMessage, {
                position: "top-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            dispatch(clearSuccess());
            setTimeout(() => {
                navigate(`/admin-categories/${categoryId}/sub-categories`);
            }, 2000);
        }
    }, [success, dispatch, navigate, categoryId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const payload = new FormData();
        payload.append('name_ar', formData.name_ar);
        payload.append('name_en', formData.name_en);
        // Only append image if it's a File object (not 'existing' string)
        if (formData.image && formData.image !== 'existing' && formData.image instanceof File) {
            payload.append('image', formData.image);
        }
        
        try {
            await dispatch(updateSubCategory({ 
                categoryId,
                subCategoryId, 
                data: payload 
            })).unwrap();
            // Success toast will be shown by useEffect that watches success state
        } catch (err) {
            // Error toast will be shown by useEffect that watches error state
        }
    };

    return (
        <div className="main_content">
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={true}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate(`/admin-categories/${categoryId}/sub-categories`)}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة للفئات الفرعية
                    </motion.button>
                </div>

                {/* Edit Form */}
                <div className="card shadow-lg border-0">
                    <div className="card-header">
                        <h5 className="mb-0">تعديل الفئة الفرعية</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">الاسم بالعربية</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">الاسم بالإنجليزية</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_en"
                                        value={formData.name_en}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label">الصورة</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) {
                                                setImageLoading(false);
                                                return;
                                            }
                                            
                                            setImageLoading(true);
                                            const previewUrl = URL.createObjectURL(file);
                                            
                                            // Wait for image to load
                                            const img = new Image();
                                            img.onload = () => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    image: file,
                                                    imagePreview: previewUrl
                                                }));
                                                setImageLoading(false);
                                            };
                                            img.onerror = () => {
                                                setImageLoading(false);
                                                toast.error('فشل في تحميل الصورة');
                                            };
                                            img.src = previewUrl;
                                        }}
                                    />
                                    {formData.imagePreview && (
                                        <div className="mt-2 text-center">
                                            <img
                                                src={formData.imagePreview}
                                                alt="preview"
                                                style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '12px' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex gap-2 justify-content-center">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => navigate(`/admin-categories/${categoryId}/sub-categories`)}
                                    disabled={updating}>
                                    إلغاء
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn text-white"
                                    style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                                    disabled={updating || imageLoading}>
                                    {updating ? 'جاري الحفظ...' : imageLoading ? 'جاري تحميل الصورة...' : 'حفظ التعديلات'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminEditSubCategory;

